import styled from '@emotion/styled';
import {
  Button,
  ButtonProps,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  ModalProps,
  Popconfirm,
  SelectProps,
  Spin,
} from 'antd';
import { useAuthorization } from 'components/Authorization';
import FormField, { SmartFieldProps } from 'components/FormField';
import { Formik, FormikConfig, FormikHelpers, FormikProps } from 'formik';
import { isValidPhoneNumber } from 'libphonenumber-js/mobile';
import React, { memo, ReactNode, useCallback, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import passwordValidator from 'utils/validator/password';
import PhoneRegionSelect from 'views/Startups/components/PhoneRegionSelect';

import { PlusOutlined } from '@ant-design/icons';
import { useMemoizedFn } from 'ahooks';
import { FormInstance, useForm } from 'antd/lib/form/Form';
import RoleCascader from 'bizComponents/RoleCascader';
import DecorateTitle from 'components/DecorateTitle';
import { useGetPermission, useRouteAccessProps } from 'hooks/business/useNewPermisson';
import useModal from 'hooks/useModal';
import useSmartField from 'hooks/useSmartField';
import { first, sortBy, keys } from 'lodash-es';
import { localPhoneParse } from 'utils/phoneHelpers';
import PermissionsFields from '../Department/components/FormFields/PermissionsFields';
import { useDepartmentAndRolesList } from '../Department/hooks/data';
import useGetRoleDetail from '../Department/hooks/useGetRoleDetail';
import useAccountValidator from '../hooks/useAccountValidator';
import { excludeRoleList, translateRole } from '../utils';
import ComplexModalForm from './SpecialForms/ComplexModalForm';
import RolePermScopeType from '../Department/RoleDetail/RolePermScopeType';
import AssignConversationConfig from '../Department/RoleDetail/AssignConversationConfig';
import { ModeAlert } from '../Department/components/FormFields/AssignConversationConfigFields';
import AccountTypeField from './AccountTypeField';
import TimeZoneField from './TimeZoneField';
import { getLocalTimeZone } from 'utils/timeZone';
import { isMasterOnline, isQuickChat, isSiteGiant } from 'productLine';
import { Auth2Detail } from './SpecialForms/Auth2Detail';
import { BindLogins } from 'services/api/assistant.account';

const StyledForm = styled(Form)`
  .${({ theme }) => theme.getAntdPrefixedClassName('form')} {
    &-item {
      margin-bottom: 20px;
    }
  }
  .ant-input-group-addon .ant-select {
    width: 150px !important;
  }
  .group-field {
    position: relative;
    .ant-select {
      flex: 1;
    }
    .ant-form-item-control-input-content {
      > .ant-btn:last-of-type {
        width: 100%;
      }
    }
  }
`;

const RoleSelectContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;
  > button {
    padding-right: 0;
  }
`;
const PermissionDrawer = styled(Drawer)`
  .ant-drawer-content-wrapper {
    max-width: 100vw;
  }
  .ant-form-item {
    flex-direction: column;
  }
  .ant-modal-body {
    padding-top: 0;
  }
  .ant-form-item-label {
    text-align: left;
  }
  .ant-form-item-control {
    margin-left: 10px;
  }
`;
const FieldTitle = styled(DecorateTitle)`
  margin-left: 16px;
`;

export type RoleCascaderType = Partial<StoreHomeV2.DepartmentAndRoleType>;

export interface AccountFormValues
  extends Util.PartialPartial<
    Pick<
      StoreHomeV2.Account,
      | 'account'
      | 'name'
      | 'phoneNumber'
      | 'roleInDepartments'
      | 'type'
      | 'timeZone'
      | 'incomingLineLimit'
    >,
    'roleInDepartments'
  > {
  id?: StoreHomeV2.Account['id'];
  password: string;
  confirmPassword?: string;
  email?: string;
  bindLogins?: BindLogins;
}

/**
 * v2版本
 */
export interface AccountFormValuesV2
  extends Pick<
    StoreHomeV2.Account,
    'account' | 'name' | 'phoneNumber' | 'type' | 'timeZone' | 'incomingLineLimit'
  > {
  id?: StoreHomeV2.Account['id'];
  password: string;
  confirmPassword?: string;
  email?: string;
  roleInDepartments?: RoleCascaderType[];
  permissionWithScopes?: StoreHomeV2.RoleWithDepartment['permissionWithScopes'];
  assignConversationConfig?: StoreHomeV2.RoleWithDepartment['assignConversationConfig'];
  isSuperAdmin?: boolean;
  bindLogins?: BindLogins;
}

type FV = AccountFormValuesV2;
export type AccountFormHelpers = FormikHelpers<FV>;

type FormRenderChildren<FV> = (nodes: ReactNode, formProps: FormikProps<FV>) => ReactNode;

export interface AccountFormProps extends Omit<FormikConfig<FV>, 'initialValues'> {
  initialValues?: FV;
  onRefresh?: () => any;
  children?: ReactNode | FormRenderChildren<FV>;
  hiddenFields?: string[];
  seat?: Assistant.SeatInfo;
  nonReceptionProcessMonthlyLimit?: number;
  isTrail: boolean;
  showAuth2?: boolean;
}

interface GroupFieldProps extends SelectProps {
  // userId: number;
  isEdit: boolean;
  formRef: FormInstance<any>;
}

const defaultInitialValues: FV = {
  account: '',
  name: '',
  password: '',
  phoneNumber: '+86',
  email: '',
  type: 1,
  roleInDepartments: [],
  isSuperAdmin: false,
  timeZone: getLocalTimeZone(),
  incomingLineLimit: 50,
};

const nameLimit = 20;

function GroupField(props: GroupFieldProps) {
  const { isEdit, formRef } = props;
  const { t } = useTranslate();
  const {
    visible: permissionVisible,
    show: permissionShow,
    hide: permissionHide,
    meta: permissionMeta,
  } = useModal<Omit<StoreHomeV2.DepartmentAndRoleType, 'departmentName'>>(false);
  const getPermission = useGetPermission();
  const editable = getPermission('帐号管理');
  const { loading: roleDetailLoading } = useGetRoleDetail(permissionMeta?.roleId, {
    onSuccess: (data) => {
      formRef?.setFieldsValue({
        permissionWithScopes: data?.permissionWithScopes,
        assignConversationConfig: data?.assignConversationConfig,
      });
    },
  });
  const { data: departmentAndRoleData } = useDepartmentAndRolesList();
  const {
    field: { value: accountType },
  } = useSmartField<FV['type']>('type');

  const data = useMemo(() => {
    return sortBy(
      departmentAndRoleData?.departmentAndRoleOfItList?.map((d) => {
        return {
          ...d,
          roles: sortBy(
            d.roles?.filter((r) => !excludeRoleList.some((er) => er === r.name)),
            ['id', 'asc'],
          ),
        };
      }) ?? [],
      ['id', 'asc'],
    );
  }, [departmentAndRoleData]);
  const permissionModal = useMemo(() => {
    if (permissionMeta === undefined) {
      return undefined;
    }
    return (
      <PermissionDrawer
        title={t('department.查看客服部-x权限', {
          x: translateRole(permissionMeta.roleName, t),
        })}
        width={1200}
        visible={permissionVisible}
        onClose={() => permissionHide()}
        footer={null}
      >
        <Spin spinning={roleDetailLoading}>
          <FieldTitle>{t('department.权限配置')}</FieldTitle>
          <RolePermScopeType departmentId={permissionMeta.departmentId} disabled />
          <AssignConversationConfig
            departmentId={permissionMeta.departmentId}
            disabled
            roleId={undefined}
          />
          <PermissionsFields
            departmentId={permissionMeta.departmentId}
            initialValue={[]}
            disabled
            required
            label={t('department.选择角色权限')}
            name="permissionWithScopes"
            shouldUpdate
          />
        </Spin>
      </PermissionDrawer>
    );
  }, [permissionVisible, permissionHide, permissionMeta, roleDetailLoading, t]);
  const deleteRole = useMemoizedFn(
    (
      value: RoleCascaderType[],
      index: number,
      onChange: SmartFieldProps<typeof value>['field']['onChange'],
    ) => {
      const newValue = [...value];
      newValue.splice(index, 1);
      onChange(newValue);
    },
  );
  const toggleRole = useMemoizedFn(
    (
      value: RoleCascaderType[],
      { roleId, departmentId }: RoleCascaderType,
      index: number,
      onChange: SmartFieldProps<typeof value>['field']['onChange'],
    ) => {
      if (
        value.some((r, i) => {
          // 不检查当前角色
          if (i === index) {
            return false;
          }
          return r.departmentId === departmentId;
        })
      ) {
        return;
      }
      const output = [...value];
      output[index] = {
        roleId,
        departmentId,
      };
      onChange(output);
    },
  );
  const addRole = useMemoizedFn(
    (value: RoleCascaderType[], onChange: SmartFieldProps<typeof value>['field']['onChange']) => {
      const output: RoleCascaderType = {};
      onChange([...value, output]);
    },
  );
  const openPermission = useMemoizedFn(({ departmentId, roleId }: RoleCascaderType) => {
    if (departmentId === undefined || roleId === undefined) {
      return;
    }
    const currentRole = data
      .find((d) => d.id === departmentId)
      ?.roles?.find((r) => r.id === roleId);
    permissionShow({
      departmentId,
      roleId,
      roleName: currentRole?.name || '',
    });
  });
  return (
    <FormField<FV['roleInDepartments']>
      name="roleInDepartments"
      className="group-field"
      label={t('department.账号角色')}
    >
      {({ field }) => {
        const { onChange, onBlur, value = [], ...rest } = field;
        return (
          <>
            {value.map((v, i) => {
              return (
                <RoleSelectContainer key={v.roleId}>
                  <RoleCascader
                    showSearch
                    permissionJudge
                    accountType={accountType}
                    disabledFields={value.map((d) => {
                      return {
                        departmentId: d.departmentId!,
                      };
                    })}
                    dataSource={data}
                    value={v.roleId}
                    onChange={(role) => {
                      toggleRole(value, role, i, onChange);
                      requestAnimationFrame(() => {
                        onBlur();
                      });
                    }}
                    {...rest}
                  />
                  <Button type="link" disabled={!v.roleId} onClick={() => openPermission(v)}>
                    {t('department.查看权限')}
                  </Button>
                  {editable && (
                    <Popconfirm
                      title={t('department.你确定要删除该部门吗？')}
                      okText={t('translation.确定')}
                      cancelText={t('translation.取消')}
                      onConfirm={() => deleteRole(value, i, onChange)}
                      disabled={!isEdit}
                    >
                      {/* 在编辑帐号时需要提示，但是添加帐号不需要提示仍然可以删除
                       */}
                      <Button
                        type="link"
                        onClick={() => {
                          if (isEdit) {
                            return;
                          }
                          deleteRole(value, i, onChange);
                        }}
                      >
                        {t('department.删除选择')}
                      </Button>
                    </Popconfirm>
                  )}
                </RoleSelectContainer>
              );
            })}
            {permissionMeta && permissionModal}
            {editable && (
              <Button type="dashed" onClick={() => addRole(value, onChange)}>
                <PlusOutlined />
                {t('department.新增帐号所在部门')}
              </Button>
            )}
          </>
        );
      }}
    </FormField>
  );
}

function trimAll(obj: Record<string, string>) {
  const newObj = { ...obj };
  for (let i in newObj) {
    if (typeof newObj[i] === 'string') {
      newObj[i] = newObj[i].trim();
    }
  }
  return newObj;
}

function AccountForm(props: AccountFormProps) {
  const {
    initialValues: initialValuesProp,
    hiddenFields = [],
    children,
    seat,
    nonReceptionProcessMonthlyLimit,
    isTrail,
    onRefresh,
    onSubmit,
    showAuth2 = false,
    ...otherProps
  } = props;
  const { t } = useTranslate();
  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const isRenderProps = typeof children === 'function';
  const [formRef] = useForm();
  const { newPermissionEnable } = useRouteAccessProps();

  const getPermission = useGetPermission();

  const { seatLimit = 0, enableSeat = 0 } = seat || {};

  const initialValues = useMemo(() => {
    if (initialValuesProp) {
      const { bindLogins, ...rest } = initialValuesProp;
      return { ...rest };
    }

    if (isTrail) {
      return defaultInitialValues;
    }

    return {
      ...defaultInitialValues,
      type: seatLimit > enableSeat ? 1 : 2,
    } as const;
  }, [initialValuesProp, isTrail, seatLimit, enableSeat]);

  const isEdit = initialValues.id !== undefined;
  /**
   * @param  {string} account
   * @param {boolean} nullable : 是否可以为空
   * @return {string|undefined} : 错误信息
   */
  const accountValidator = useAccountValidator();
  const scrollToError = useMemoizedFn((errors: Record<string, any>): void => {
    //Form的scrollToFirstError方法无效
    const firstErrorKey = first(keys(errors));
    if (firstErrorKey) {
      const errorElement = document.getElementById(firstErrorKey);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  });
  const validate = useCallback(
    (values: FV) => {
      const errors: Partial<Record<keyof typeof values, any>> = {};

      const { account, name, phoneNumber, password, confirmPassword, email } = values;
      const { account: initialAccount } = initialValues;

      const skipAccountValidate = isEdit && account === initialAccount;

      if (!skipAccountValidate) {
        // account 必填 2022-11-30 @jianjun.pan
        const accountError = accountValidator(account, false);
        if (accountError) {
          errors.account = accountError;
        }
      }

      if (name.length > nameLimit) {
        errors.name = t('validate.nickLimit', { limit: nameLimit });
      }

      const phoneData = localPhoneParse(phoneNumber || '');
      if (isQuickChat && !phoneData?.phone) {
        errors.phoneNumber = t('validate.phoneEmpty');
      }
      if (phoneData?.phone && !isValidPhoneNumber(phoneNumber)) {
        errors.phoneNumber = t('validate.phoneInvalid');
      }

      if (email && email.trim() && !/^[\w.%+-]+@[\w.-]+\.[a-z]{2,}$/i.test(email)) {
        errors.email = t('validate.emailInvalid');
      }

      if (isEdit) {
        scrollToError(errors);
        return errors;
      }

      const { valid: passwordValid, message: passwordError } = passwordValidator(password, account);

      if (passwordValid === false && passwordError) {
        errors.password = passwordError;
      }
      if (password !== confirmPassword && confirmPassword) {
        errors.confirmPassword = t('validate.confirmPassword');
      }
      scrollToError(errors);
      return errors;
    },
    [initialValues, isEdit, accountValidator, t, scrollToError],
  );
  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={initialValues}
      validate={validate}
      onSubmit={useMemoizedFn<AccountFormProps['onSubmit']>((values, ...otherArgs) => {
        const finalValues = { ...values };
        const phoneData = localPhoneParse(values.phoneNumber);
        if (!phoneData?.phone) {
          finalValues.phoneNumber = '';
        }
        if (!values.name) {
          finalValues.name = values.account;
        }
        return onSubmit(trimAll(finalValues), ...otherArgs);
      })}
      {...otherProps}
    >
      {(props) => {
        const { values } = props;
        const form = (
          <StyledForm layout="vertical" form={formRef}>
            {isMasterOnline ? null : <FieldTitle>{t('department.基础信息')}</FieldTitle>}
            {isEdit && !isMasterOnline && (
              <>
                <ComplexModalForm
                  key={initialValues?.id || 'unknown'}
                  initialValues={values as any}
                  onUpdated={(subValues) => {
                    const { setFieldValue, setFieldTouched } = props;
                    Object.keys(subValues).forEach((key) => {
                      const v = subValues[key];
                      setFieldValue(key, v);
                      setFieldTouched(key, false);
                    });
                    onRefresh && onRefresh();
                  }}
                />
                <Divider className="mt-4 mb-2" />
              </>
            )}
            {!isEdit && (
              <FormField<FV['account']> name="account" required label={t('field.account')}>
                {({ field }) => {
                  return (
                    <Input
                      id="account"
                      placeholder={t('placeholder.account')}
                      allowClear
                      {...field}
                    />
                  );
                }}
              </FormField>
            )}
            {!isEdit && (
              <FormField<FV['password']> name="password" required label={t('field.password')}>
                {({ field }) => {
                  return (
                    <Input.Password
                      id="password"
                      placeholder={t(
                        'ViewStartups.registry.form.item.password.validator.error.length',
                      )}
                      allowClear
                      {...field}
                    />
                  );
                }}
              </FormField>
            )}
            <FormField<FV['incomingLineLimit']>
              name="incomingLineLimit"
              required
              label={t('帐号进线数')}
              description={t(
                '客服账号最多可以接待的「待处理」会话上限，达到上限后，一般情况下将不再分配新会话',
              )}
              validate={(value) =>
                value || value === 0 ? undefined : t('department.请输入x', { x: '0-1000' })
              }
            >
              {({ field }) => {
                return (
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder={t('department.请输入x', { x: '0-1000' })}
                    precision={0}
                    min={0}
                    max={1000}
                    {...(field as any)}
                  />
                );
              }}
            </FormField>
            {seat && getPermission('帐号管理') && !values.isSuperAdmin && (
              <AccountTypeField
                seat={seat}
                nonReceptionProcessMonthlyLimit={nonReceptionProcessMonthlyLimit}
              />
            )}
            {!hiddenFields.includes('timeZone') && <TimeZoneField />}
            <FormField<FV['name']>
              name="name"
              // required
              label={t('field.name')}
            >
              {({ field, helpers, meta }) => {
                const name = values?.name || meta.touched ? values?.name : values?.account;
                const onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
                  helpers.setTouched(true);
                  field.onChange(e);
                };
                return (
                  <Input
                    placeholder={t('placeholder.name')}
                    allowClear
                    {...field}
                    onChange={onChange}
                    value={name}
                  />
                );
              }}
            </FormField>
            {isMasterOnline ? null : (
              <FormField<FV['email']> name="email" label={t('field.email')}>
                {({ field }) => {
                  return (
                    <Input
                      placeholder={t('placeholder.email')}
                      type="email"
                      allowClear
                      {...field}
                    />
                  );
                }}
              </FormField>
            )}
            {!isEdit && (
              <>
                <FormField<FV['phoneNumber']>
                  name="phoneNumber"
                  required={isQuickChat}
                  label={t('field.phone')}
                >
                  {({ field }) => {
                    const { value: orgValue, onChange, ...others } = field;
                    const phoneData = localPhoneParse(orgValue);
                    const code = phoneData?.code || '+86';
                    const phoneWithoutPrefix = phoneData?.phone || '';
                    return (
                      <Input
                        placeholder={t('placeholder.phone')}
                        id="phoneNumber"
                        addonBefore={
                          <PhoneRegionSelect
                            value={code}
                            onChange={(code) => {
                              onChange(`${code}${phoneWithoutPrefix}`);
                            }}
                          />
                        }
                        value={phoneWithoutPrefix}
                        onChange={({ target: { value } }) => onChange(`${code}${value}`)}
                        allowClear
                        {...others}
                      />
                    );
                  }}
                </FormField>
              </>
            )}
            {hiddenFields.includes('roleInDepartments') ||
            values.roleInDepartments?.some((r) =>
              excludeRoleList.some((er) => er === r.roleName),
            ) ||
            !payload?.id ||
            !newPermissionEnable ? null : (
              <>
                <FieldTitle>{t('department.部门角色')}</FieldTitle>
                <GroupField isEdit={isEdit} formRef={formRef} />
                <ModeAlert />
              </>
            )}

            {showAuth2 && !isMasterOnline && !isSiteGiant && (
              <Auth2Detail auth2Props={initialValuesProp?.bindLogins} />
            )}
          </StyledForm>
        );

        return isRenderProps ? (children as FormRenderChildren<FV>)(form, props) : form;
      }}
    </Formik>
  );
}

export interface AccountFormModalProps
  extends Util.PartialRequired<ModalProps, 'onCancel'>,
    Pick<
      AccountFormProps,
      | 'initialValues'
      | 'onSubmit'
      | 'onRefresh'
      | 'seat'
      | 'nonReceptionProcessMonthlyLimit'
      | 'isTrail'
    > {
  type?: 'modal' | 'drawer';
  hiddenFields?: string[];
  showAuth2?: boolean;
}

function FormModal(props: AccountFormModalProps) {
  const {
    type = 'modal',
    initialValues,
    onSubmit,
    onCancel,
    onRefresh,
    afterClose: afterCloseProp,
    hiddenFields,
    seat,
    okText,
    nonReceptionProcessMonthlyLimit,
    isTrail,
    showAuth2 = false,
    ...otherProps
  } = props;
  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const { t } = useTranslate();
  const isSelf = payload?.id && initialValues?.id === payload?.id;
  return (
    <AccountForm
      onRefresh={onRefresh}
      initialValues={initialValues}
      onSubmit={onSubmit}
      hiddenFields={hiddenFields}
      seat={seat}
      nonReceptionProcessMonthlyLimit={nonReceptionProcessMonthlyLimit}
      isTrail={isTrail}
      showAuth2={showAuth2}
    >
      {(form, props) => {
        const { dirty, isSubmitting, handleSubmit, resetForm } = props;

        const isEdit = initialValues && initialValues.id !== undefined;

        const onClose = (e: any) => {
          if (!isSelf && !isEdit && dirty) {
            const { destroy } = Modal.confirm({
              title: t('account.cancelCreateConfirm'),
              onOk: (e) => {
                onCancel(e);
                destroy();
              },
            });
          } else {
            onCancel(e);
          }
        };

        const modalOrDrawerProps = {
          width: 570,
          title: isEdit ? t('modals.帐号信息') : t('account.createAccount'),
          maskClosable: false,
          ...otherProps,
        };

        const okButtonProps: ButtonProps = {
          type: 'primary',
          loading: isSubmitting,
          onClick: () => handleSubmit(),
        };

        const okButtonText = okText || t('account.save');

        const afterClose = () => {
          resetForm();

          afterCloseProp?.();
        };

        if (type === 'modal') {
          return (
            <Modal
              onCancel={onClose}
              okButtonProps={okButtonProps}
              okText={okButtonText}
              afterClose={afterClose}
              {...modalOrDrawerProps}
            >
              {form}
            </Modal>
          );
        }

        return (
          <Drawer
            onClose={onClose}
            afterVisibleChange={(visible) => {
              if (!visible) {
                afterClose();
              }
            }}
            footer={
              <>
                <Button {...okButtonProps}>{okButtonText}</Button>
                <Button onClick={onClose} style={{ marginLeft: '16px' }}>
                  {t('translation.取消')}
                </Button>
              </>
            }
            {...modalOrDrawerProps}
          >
            {form}
          </Drawer>
        );
      }}
    </AccountForm>
  );
}

export const AccountFormModal = memo(FormModal);

export default memo(AccountForm);
